import { onMounted, ref } from 'vue';

/**
 * The order a Sponsors block shows its sponsors in.
 *
 * With `random_order` on, every visitor sees them shuffled, so no
 * sponsor always gets the first spot. The shuffle is SEEDED once per
 * mount: a re-render must not reshuffle under somebody reading the
 * row, and a plain `Math.random()` inside a computed would.
 *
 * In the editor the row stays in list order, so the preview matches
 * the list beside it and the ▲ ▼ buttons still mean something. The
 * editor says so under the setting.
 *
 * And the seed is drawn AFTER MOUNT, never in setup. The page is
 * rendered on the server first (resources/js/ssr.js) and hydrated, and
 * hydration keeps the server's attributes while patching its text — so
 * a server shuffle and a different browser shuffle put one sponsor's
 * logo and link under another sponsor's name. Until the seed arrives
 * the row is in list order on both sides; the shuffle lands a frame
 * later, as an ordinary re-render.
 */
export function useSponsorSeed() {
    const seed = ref(null);
    onMounted(() => { seed.value = Math.floor(Math.random() * 2 ** 32); });
    return seed;
}

/** A copy of `items`, shuffled by `seed` when `random` is on. */
export function orderSponsors(items, { random = false, inEditor = false, seed = 0 } = {}) {
    const list = Array.isArray(items) ? [...items] : [];
    if (! random || inEditor || seed == null || list.length < 2) return list;
    // mulberry32: small, fast and deterministic for a given seed.
    let a = seed >>> 0;
    const next = () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    for (let i = list.length - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1));
        [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
}
